import React from "react";

/**
 * The Kobo analytics endpoints answer with a `_syncStatus` marker instead of data while a
 * period is being built, or when building it failed. This module is the single place that
 * reads that marker and decides how long to keep polling for it, so every chart on the
 * page reaches the same conclusion at the same time.
 */

export type SyncState = "ready" | "syncing" | "failed" | "stalled";

/** How often to re-ask while the server says it is syncing. */
export const SYNC_POLL_MS = 5000;

/**
 * Give up after this many polls (~2 minutes). A sync that has not landed by then is not
 * going to land on this page view, and a spinner that never resolves tells the user
 * nothing — past the cap they get a message and a Retry button instead.
 */
export const MAX_SYNC_POLLS = 24;

/**
 * Pull the sync marker out of a response, which is an object for the stats endpoints and
 * an array whose first element carries the marker for the trends endpoints.
 */
export function readSyncState(responseData: any): {
  state: SyncState;
  message?: string;
} {
  const payload = responseData?.data;
  const marker = Array.isArray(payload) ? payload[0] : payload;

  if (marker?._syncStatus === "syncing") {
    return { state: "syncing", message: marker._message };
  }
  if (marker?._syncStatus === "failed") {
    return { state: "failed", message: marker._message };
  }
  return { state: "ready" };
}

/**
 * Polling policy for a sync-aware query.
 *
 * Pass `refetchInterval` straight to useQuery. `resolve` turns the latest response into
 * the state the UI should render, collapsing "still syncing after the cap" into
 * "stalled" so the component can offer a way out instead of spinning forever.
 */
export function useSyncPolling(maxPolls: number = MAX_SYNC_POLLS) {
  const polls = React.useRef(0);

  const refetchInterval = React.useCallback(
    (query: any) => {
      const { state } = readSyncState(query?.state?.data);

      if (state !== "syncing") {
        polls.current = 0;
        return false as const;
      }
      if (polls.current >= maxPolls) return false as const;

      polls.current += 1;
      return SYNC_POLL_MS;
    },
    [maxPolls],
  );

  const resolve = React.useCallback(
    (responseData: any): { state: SyncState; message?: string } => {
      const read = readSyncState(responseData);
      if (read.state === "syncing" && polls.current >= maxPolls) {
        return {
          state: "stalled",
          message:
            "The sync is taking longer than expected. It may still be running in the background.",
        };
      }
      return read;
    },
    [maxPolls],
  );

  /** Clear the budget so a Retry gets a fresh round of polling. */
  const reset = React.useCallback(() => {
    polls.current = 0;
  }, []);

  return { refetchInterval, resolve, reset };
}
